import { useEffect, useState } from 'react'
import { useAppDispatch } from '../../reducers/store.ts'
import { fetchDevicePolicy, postUpdateDevicePolicy, setDevicePolicy } from '../../reducers/settings/index.ts'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useDispatch, useSelector } from 'react-redux'
import { CreatePolicyInput, defaultDevicePolicy, PolicyActionsInput, PolicyConfig } from '../../types/SettingsType.ts'
import PolicyDetails from './PolicyDetails.tsx'
import PolicySummary from './PolicySummary.tsx'
import { selectSettings } from '../../reducers/settings/index.ts'
import PolicyActions from './PolicyActions.tsx'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { createPolicySchema } from '../../schemas/IntegrationSchema.ts'
import { DEFAULT_TOAST_CONFIG } from '../toast/consts.ts'
import CreatePolicyRules from './CreatePolicyRules.tsx'
import { selectIntegrations } from '../../reducers/integrations/integrations.slice.ts'
import { fetchIntegrationsList } from '../../reducers/integrations/integrations.thunk.ts'
import { IntegrationDetails } from '../../types/IntegrationTypes.ts'
import { deleteTravelPolicy, updateTravelPolicy } from '../../reducers/travel/travel.thunk.ts'
import { DeviceTravelPolicy, TRAVEL_POLICY_PERIOD, TRAVEL_RISK_LEVEL } from '../../types/TravelTypes.ts'
import SavePolicyModal from './SavePolicyModal.tsx'
import { selectTravel } from '../../reducers/travel/travel.slice.ts'
import { fetchTravelPolicyWithId } from '../../reducers/travel/travel.thunk.ts'
import PolicyGroups from './PolicyGroups.tsx'
import { selectGroup } from '../../reducers/groups/groups.slice.ts'
import { getPolicyUserGroups, updateGroupPolicies } from '../../reducers/groups/groups.thunk.ts'
import { SelectedGroup, UpdateGroupsForPoliciesRequest } from '../../types/GroupTypes.ts'

function DevicePolicyOverviewWrapper() {
  const di = useAppDispatch()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { policyId } = useParams()
  const { devicePolicy } = useSelector(selectSettings)
  const { travelPolicy } = useSelector(selectTravel)
  const { integrationsList } = useSelector(selectIntegrations)
  const { policyUserGroups } = useSelector(selectGroup)

  const [selectedPage, setSelectedPage] = useState<string>('summary')
  const [policies, setPolicies] = useState<Partial<PolicyConfig>>({})
  const [policyDetails, setPolicyDetails] = useState<CreatePolicyInput | undefined>(undefined)
  const [activateOnCreate, setActivateOnCreate] = useState<boolean>(false)
  const [policyActionDetails, setPolicyActionDetails] = useState<PolicyActionsInput | undefined>(undefined)
  const [showSavePolicyModal, setShowSavePolicyModal] = useState<boolean>(false)
  const [checkedItems, setCheckedItems] = useState<Record<number, boolean>>({})
  const [selectedGroups, setSelectedGroups] = useState<SelectedGroup[]>([])

  useEffect(() => {
    if (policyId) {
      const numericId = Number(policyId)
      if (!isNaN(numericId)) {
        di(fetchDevicePolicy(numericId))
        di(fetchTravelPolicyWithId(numericId))
        di(getPolicyUserGroups(numericId))
      } else {
        toast.error('The policy ID provided was invalid, please try again.')
        navigate('/device-policies')
      }
    }
  }, [policyId])

  useEffect(() => {
    di(fetchIntegrationsList())
  }, [])

  useEffect(() => {
    return () => {
      dispatch(setDevicePolicy(undefined))
    }
  }, [dispatch])

  useEffect(() => {
    if (devicePolicy && integrationsList) {
      const updatedPolicyDetails = {
        name: devicePolicy?.name,
        description: devicePolicy?.description,
      }
      setPolicyDetails(updatedPolicyDetails)

      const policyRules: Partial<PolicyConfig> = {
        alertSeverity: devicePolicy?.alertSeverity,
        biometricsEnabled: devicePolicy?.biometricsEnabled === 'true',
        deviceIntegrityPeriod: devicePolicy?.deviceIntegrityPeriod,
        screenLockEnabled: devicePolicy?.screenLockEnabled === 'true',
        smishingEnabled: devicePolicy?.smishingEnabled === 'true',
        travelEnabled: travelPolicy?.sensitiveTravelRequestLocation === true,
        travelRiskPeriod: undefined,
        travelRiskLevel: undefined,
        iosMinimumVersion: devicePolicy?.iosMinimumVersion,
        iosVersionOffset: devicePolicy?.iosVersionOffset ? devicePolicy?.iosVersionOffset : undefined,
        iosMinimumVersionGracePeriod: devicePolicy?.iosMinimumVersionGracePeriod
          ? devicePolicy?.iosMinimumVersionGracePeriod
          : undefined,
        iosMinimumVersionGracePeriodUnit: devicePolicy?.iosMinimumVersionGracePeriodUnit,
        androidMinimumVersion: devicePolicy?.androidMinimumVersion ? devicePolicy?.androidMinimumVersion : undefined,
        androidLastPatchVersionGracePeriod: devicePolicy?.androidLastPatchVersionGracePeriod
          ? devicePolicy?.androidLastPatchVersionGracePeriod
          : undefined,
        androidLastPatchVersionGracePeriodUnit: devicePolicy?.androidLastPatchVersionGracePeriodUnit,
      }
      setPolicies(policyRules)

      const activePage = integrationsList?.find((item) => item.pageState === 'ACTIVE')
      const caIntegration: IntegrationDetails | undefined = activePage
        ? activePage.integrations.find((i: IntegrationDetails) => i.category === 'Conditional Access')
        : undefined

      const integrationName =
        caIntegration?.serverValue === 'intune_mtd'
          ? 'Microsoft Intune Mobile Threat Defence'
          : devicePolicy?.actions || 'Device Policy'

      setPolicyActionDetails({
        threatLevel: devicePolicy?.threatLevel,
        gracePeriodValue: devicePolicy?.gracePeriod?.toString().split('_')[0] || '0',
        gracePeriodUnit: devicePolicy?.gracePeriod?.toString().split('_')[1] || 'DAYS',
        integrationName,
      })

      setActivateOnCreate(devicePolicy?.active || false)
    }
  }, [devicePolicy, integrationsList, travelPolicy])

  const methods = useForm<CreatePolicyInput>({
    resolver: yupResolver(createPolicySchema),
    mode: 'onChange',
    defaultValues: { name: '', description: '' },
  })

  const saveCompletePolicy = async () => {
    const formValues = methods.getValues()
    const effectiveName =
      formValues?.name?.trim() !== '' && formValues?.name !== undefined
        ? formValues.name.trim()
        : policyDetails?.name?.trim() || ''
    const effectiveDescription =
      formValues?.description?.trim() !== '' && formValues?.description !== undefined
        ? formValues.description.trim()
        : policyDetails?.description?.trim() || ''

    let hasIntuneMtdServerValue = false
    if (integrationsList) {
      const activePage = integrationsList.find((item) => item.pageState === 'ACTIVE')
      if (activePage) {
        const filteredActive = activePage.integrations.filter(
          (integration: IntegrationDetails) => integration.category === 'Conditional Access'
        )
        const hasIntuneMtd = filteredActive.some(
          (integration: IntegrationDetails) => integration.serverValue === 'intune_mtd'
        )
        hasIntuneMtdServerValue = hasIntuneMtd
      }
    }
    let actionsName = policyActionDetails?.integrationName
    let gracePeriodValue = policyActionDetails?.gracePeriodValue
    let gracePeriodUnit = policyActionDetails?.gracePeriodUnit

    if (
      actionsName === 'Microsoft Intune Mobile Threat Defence' ||
      actionsName === 'Intune MTD' ||
      hasIntuneMtdServerValue
    ) {
      actionsName = 'Intune MTD'
      if (!gracePeriodValue || gracePeriodValue === 'None') {
        gracePeriodValue = '0'
      }
      if (!gracePeriodUnit || gracePeriodUnit === 'None') {
        gracePeriodUnit = 'DAYS'
      }
    } else if (actionsName === 'None' && activateOnCreate) {
      actionsName = 'Device Policy'
    } else {
      actionsName = actionsName ?? 'None'
    }

    if (!effectiveName) {
      toast.error('Policy name is required', DEFAULT_TOAST_CONFIG)
      return
    }

    if (!policyId) {
      toast.error('Policy ID is required', DEFAULT_TOAST_CONFIG)
      return
    }

    if (Object.keys(policies).length === 0) {
      toast.error('At least one policy rule is required', DEFAULT_TOAST_CONFIG)
      return
    }

    const payload = {
      ...defaultDevicePolicy,
      name: effectiveName,
      description: effectiveDescription,
      actions: actionsName,
      actionAssigned: true,
      gracePeriod: gracePeriodValue && gracePeriodUnit ? `${gracePeriodValue}_${gracePeriodUnit}` : null,
      active: activateOnCreate,
      threatLevel: policyActionDetails?.threatLevel ?? 'LOW',
      alertSeverity: policies.alertSeverity ?? 'NONE',
      deviceIntegrityPeriod: policies.deviceIntegrityPeriod ?? 'NEVER',
      screenLockEnabled: String(policies.screenLockEnabled ?? false),
      smishingEnabled: String(policies.smishingEnabled ?? false),
      biometricsEnabled: String(policies.biometricsEnabled ?? false),
      travelEnabled: String(policies.travelEnabled ?? false),
      policyId: Number(policyId),
      iosMinimumVersion: policies.iosMinimumVersion,
      iosVersionOffset: policies.iosVersionOffset ? policies.iosVersionOffset.toString() : undefined,
      iosMinimumVersionGracePeriod: policies.iosMinimumVersionGracePeriod
        ? policies.iosMinimumVersionGracePeriod.toString()
        : undefined,
      iosMinimumVersionGracePeriodUnit: policies.iosMinimumVersionGracePeriodUnit,
      androidMinimumVersion: policies.androidMinimumVersion ? policies.androidMinimumVersion?.toString() : undefined,
      androidLastPatchVersionGracePeriod: policies.androidLastPatchVersionGracePeriod
        ? policies.androidLastPatchVersionGracePeriod.toString()
        : undefined,
      androidLastPatchVersionGracePeriodUnit: policies.androidLastPatchVersionGracePeriodUnit,
    }

    const policyUpdated = await di(postUpdateDevicePolicy(payload))

    if (policyUpdated) {
      const policyPayload: UpdateGroupsForPoliciesRequest = {
        policyId: Number(policyId),
        updatedGroups: Object.entries(checkedItems).map(([groupId, enabled]) => ({
          groupId: Number(groupId),
          enabled,
        })),
      }
      await di(updateGroupPolicies(policyPayload))

      if ((policies?.travelEnabled ?? false) === true) {
        const travelPayload: DeviceTravelPolicy = {
          devicePolicyId: Number(policyId),
          riskLevel: (policies?.travelRiskLevel ?? TRAVEL_RISK_LEVEL.NONE) as TRAVEL_RISK_LEVEL,
          riskPeriod: (policies?.travelRiskPeriod ?? TRAVEL_POLICY_PERIOD.SEVEN_DAYS) as TRAVEL_POLICY_PERIOD,
          sensitiveTravelRequestLocation: policies?.travelEnabled ?? false,
        }
        const travelPolicyUpdated = await di(updateTravelPolicy(travelPayload))
        if (travelPolicyUpdated) {
          toast.success('Policy updated successfully', DEFAULT_TOAST_CONFIG)
          navigate('/device-policies')
          return
        } else {
          toast.error('Failed to update policy', DEFAULT_TOAST_CONFIG)
          return
        }
      } else {
        const deletedPolicy = await di(deleteTravelPolicy({ travelPolicyId: Number(policyId) }))
        if (deletedPolicy) {
          toast.success('Policy updated successfully', DEFAULT_TOAST_CONFIG)
          navigate('/device-policies')
          return
        } else {
          toast.error('Failed to update policy', DEFAULT_TOAST_CONFIG)
          return
        }
      }
    } else {
      toast.error('Failed to update policy', DEFAULT_TOAST_CONFIG)
    }
  }

  useEffect(() => {
    const handler = () => setShowSavePolicyModal(true)
    window.addEventListener('saveDevicePolicy', handler)
    return () => window.removeEventListener('saveDevicePolicy', handler)
  }, [policyDetails, policyActionDetails, policies, activateOnCreate])

  const noop = () => {}

  const handleUpdateFromDetails = (details: CreatePolicyInput) => {
    setPolicyDetails(details)
  }

  useEffect(() => {
    if (!policyUserGroups) return
    const initialState: Record<number, boolean> = {}
    for (const p of policyUserGroups) {
      initialState[p.id] = p.assigned
    }
    setCheckedItems(initialState)
  }, [policyUserGroups])

  useEffect(() => {
    if (!policyUserGroups) return

    const selected: SelectedGroup[] = Object.entries(checkedItems)
      .filter(([_, enabled]) => enabled)
      .map(([groupId]) => {
        const group = policyUserGroups.find((g) => g.id === Number(groupId))
        return group
          ? {
              name: group.name,
              memberCount: group.memberCount,
            }
          : null
      })
      .filter((g): g is SelectedGroup => g !== null)

    setSelectedGroups(selected)
  }, [checkedItems, policyUserGroups])

  return (
    <>
      <FormProvider {...methods}>
        <div className="absolute top-18 left-52 rounded-tl-lg border-mainBorder border-t border-l bg-contentBackground w-[calc(100%_-_208px)] h-[calc(100%_-_74px)] overflow-auto">
          <div className="h-12 w-full border-b border-mainBorder flex gap-6 p-4 mb-10 bg-white">
            <div>
              <p className="text-mainText text-sm cursor-pointer" onClick={() => setSelectedPage('summary')}>
                Summary
              </p>
              {selectedPage === 'summary' && <div className="h-0.5 bg-mainText rounded-t-lg mt-2.5" />}
            </div>

            <div>
              <p className="text-mainText text-sm cursor-pointer" onClick={() => setSelectedPage('rules')}>
                Rules
              </p>
              {selectedPage === 'rules' && <div className="h-0.5 bg-mainText rounded-t-lg mt-2.5" />}
            </div>
            <div>
              <p className="text-mainText text-sm cursor-pointer" onClick={() => setSelectedPage('actions')}>
                Integration
              </p>
              {selectedPage === 'actions' && <div className="h-0.5 bg-mainText rounded-t-lg mt-2.5" />}
            </div>
            <div>
              <p className="text-mainText text-sm cursor-pointer" onClick={() => setSelectedPage('groups')}>
                Groups
              </p>
              {selectedPage === 'groups' && <div className="h-0.5 bg-mainText rounded-t-lg mt-2.5" />}
            </div>
            <div>
              <p className="text-mainText text-sm cursor-pointer" onClick={() => setSelectedPage('details')}>
                Details
              </p>
              {selectedPage === 'details' && <div className="h-0.5 bg-mainText rounded-t-lg mt-2.5" />}
            </div>
          </div>
          {selectedPage === 'summary' && (
            <PolicySummary
              policyDetails={policyDetails}
              policyRules={policies}
              policyActionDetails={policyActionDetails}
              activateOnCreate={activateOnCreate}
              setActivateOnCreate={setActivateOnCreate}
              screen={'EDIT'}
              selectedGroups={selectedGroups}
            />
          )}
          {selectedPage === 'rules' && (
            <CreatePolicyRules
              setIsNextEnabled={noop}
              onPoliciesSelected={setPolicies}
              policyRules={policies}
              policyId={Number(policyId)}
            />
          )}
          {selectedPage === 'actions' && (
            <PolicyActions
              onActionDetailsSelected={setPolicyActionDetails}
              existingActionDetails={policyActionDetails}
            />
          )}
          {selectedPage === 'groups' && <PolicyGroups checkedItems={checkedItems} setCheckedItems={setCheckedItems} />}
          {selectedPage === 'details' && (
            <PolicyDetails
              setIsNextEnabled={noop}
              policyDetails={policyDetails}
              onDetailsChange={handleUpdateFromDetails}
            />
          )}
        </div>
      </FormProvider>
      {showSavePolicyModal && (
        <SavePolicyModal setShowSavePolicyModal={setShowSavePolicyModal} savePolicy={saveCompletePolicy} />
      )}
    </>
  )
}

export default DevicePolicyOverviewWrapper
